import styles from './appIcons.module.css';

/**
 * The icons of what you can send: one blue glyph on a pale blue square, the
 * same for every kind. The glyphs are Fluent UI System Icons (regular, 24 px,
 * MIT licence, https://github.com/microsoft/fluentui-system-icons): a
 * password field, a document with a lock, two speech bubbles, a screen with
 * an arrow coming in.
 */

export type AppIconKind = 'password' | 'file' | 'chat' | 'receive';

/** A keyboard, for typing a code: the same blue line on the same square, drawn rather than taken from the set. */
function KeyboardGlyph({ className }: { readonly className: string | undefined }): React.JSX.Element {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" focusable="false">
      <rect width="19" height="14" x="2.5" y="5" rx="2.5" />
      <path d="M6.5 9h.01M10 9h.01M14 9h.01M17.5 9h.01M8.25 12.25h.01M12 12.25h.01M15.75 12.25h.01M8 15.5h8" strokeWidth="1.75" />
    </svg>
  );
}

const GLYPHS: Record<AppIconKind, string> = {
  password:
    'M11.78 10.22a.75.75 0 0 0-1.06 1.06l.72.72-.72.72a.75.75 0 1 0 1.06 1.06l.72-.72.72.72a.75.75 0 0 0 1.06-1.06l-.72-.72.72-.72a.75.75 0 1 0-1.06-1.06l-.72.72zm-6.56 0c.3-.3.77-.3 1.06 0l.72.72.72-.72a.75.75 0 1 1 1.06 1.06l-.72.72.72.72a.75.75 0 1 1-1.06 1.06L7 13.06l-.72.72a.75.75 0 0 1-1.06-1.06l.72-.72-.72-.72a.75.75 0 0 1 0-1.06M16.5 12.5a.75.75 0 0 0 0 1.5h1.75a.75.75 0 0 0 0-1.5zM5.25 5A3.25 3.25 0 0 0 2 8.25v7.5C2 17.55 3.46 19 5.25 19h13.5c1.8 0 3.25-1.46 3.25-3.25v-7.5C22 6.45 20.55 5 18.75 5zM3.5 8.25c0-.97.78-1.75 1.75-1.75h13.5c.97 0 1.75.78 1.75 1.75v7.5c0 .97-.78 1.75-1.75 1.75H5.25c-.97 0-1.75-.78-1.75-1.75z',
  file: 'M18.5 20a.5.5 0 0 1-.5.5h-6v1q0 .26-.05.5H18a2 2 0 0 0 2-2V9.83a2 2 0 0 0-.59-1.42L13.6 2.6c-.37-.37-.9-.59-1.42-.59H6a2 2 0 0 0-2 2v7.55a3.5 3.5 0 0 1 1.5-.9V4c0-.27.22-.5.5-.5h6V8c0 1.1.9 2 2 2h4.5zm-5-15.38 3.88 3.88H14a.5.5 0 0 1-.5-.5zM4 15h-.5c-.83 0-1.5.67-1.5 1.5v5c0 .83.67 1.5 1.5 1.5h6c.83 0 1.5-.67 1.5-1.5v-5c0-.83-.67-1.5-1.5-1.5H9v-1a2.5 2.5 0 0 0-5 0zm1.5-1a1 1 0 1 1 2 0v1h-2zm2 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0',
  chat: 'M9.56 3a7.5 7.5 0 0 0-6.8 10.67l-.72 2.84c-.23.92.59 1.75 1.5 1.53l2.93-.7q1.44.64 3.1.66a7.5 7.5 0 0 0 0-15m-6 7.5a6 6 0 1 1 3.33 5.37l-.24-.12-.27.07-2.78.66.69-2.7.07-.28-.13-.25a6 6 0 0 1-.67-2.75m11 10.5a7.5 7.5 0 0 1-5.1-2 8 8 0 0 0 2.18-.26 6 6 0 0 0 5.6.13l.24-.12.26.07c.92.22 1.96.44 2.74.6-.18-.74-.41-1.75-.64-2.64l-.07-.28.13-.25a5.97 5.97 0 0 0-2.09-7.8 8 8 0 0 0-.8-2.04 7.5 7.5 0 0 1 4.35 10.26l.67 2.8c.2.9-.57 1.7-1.47 1.5-.72-.15-1.87-.39-2.91-.63q-1.43.64-3.09.66',
  receive:
    'M23 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0m-5-3a.5.5 0 0 0-1 0v4.8l-1.65-1.65a.5.5 0 0 0-.7.7l2.5 2.5c.2.2.5.2.7 0l2.5-2.5a.5.5 0 0 0-.7-.7L18 8.29zm2.5 12.25v-3.48a7 7 0 0 0 1.5-1.08v4.56c0 1.2-.93 2.17-2.1 2.25h-4.4v2.5h1.75a.75.75 0 0 1 .1 1.5H6.75a.75.75 0 0 1-.1-1.5H8.5V18H4.25c-1.2 0-2.17-.92-2.24-2.1L2 15.76V5.25c0-1.2.93-2.17 2.1-2.25h7.92q-.45.7-.7 1.5H4.24c-.38 0-.7.28-.74.65l-.01.1v10.5c0 .38.28.7.65.74l.1.01h15.5c.38 0 .7-.28.74-.65zM14 18h-4v2.5h4z',
};

/** The bare glyph in the current text colour, for a button: the same picture as on the tile. */
export function AppGlyph({ kind, size = 18, className }: { readonly kind: AppIconKind; readonly size?: number; readonly className?: string | undefined }): React.JSX.Element {
  return (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false">
      <path d={GLYPHS[kind]} />
    </svg>
  );
}

/** The glyph on its pale blue square (44 px unless `--icon-size` says otherwise). */
export function AppIcon({ kind, className }: { readonly kind: AppIconKind | 'code'; readonly className?: string | undefined }): React.JSX.Element {
  if (kind === 'code') {
    return (
      <span className={[styles.tile, className ?? ''].join(' ')} aria-hidden="true">
        <KeyboardGlyph className={styles.glyph} />
      </span>
    );
  }
  return (
    <span className={[styles.tile, className ?? ''].join(' ')} aria-hidden="true">
      <svg className={styles.glyph} viewBox="0 0 24 24" fill="currentColor" focusable="false">
        <path d={GLYPHS[kind]} />
      </svg>
    </span>
  );
}
